/**
 * DecisionPolicy set — Retail Ranker's expertise, in code.
 *
 * Each policy is named, versioned, inspectable. Adding retail expertise
 * to the product means adding a policy here, not editing a prompt.
 *
 * Policies keyed to the smallest working engine. More will be added as
 * we test against real retailers.
 */

import type { DecisionPolicy } from "./types";
import { shareStem } from "./text";

function intentBag(c: { intent: { label: string; description: string; tags?: string[] } }): string {
  return `${c.intent.label} ${c.intent.description} ${(c.intent.tags ?? []).join(" ")}`;
}

export const DEFAULT_POLICIES: DecisionPolicy[] = [
  // --- Hard rejects ----------------------------------------------------
  {
    policy_id: "reject-constraint-violation",
    version: "0.1.0",
    description:
      "If a candidate's intent overlaps a constraint the retailer stated, reject it. Retailer knowledge wins over search demand.",
    applies_to: (c, ctx) => {
      const hay = intentBag(c);
      return ctx.constraints.some((con) => shareStem(hay, con.label));
    },
    effect: { kind: "reject", reason_code: "violates_constraint", note: "matches a stated constraint" },
  },
  {
    policy_id: "reject-already-featured",
    version: "0.1.0",
    description:
      "If the retailer is already featuring this intent well, don't spend attention here. Recommend elsewhere.",
    applies_to: (c) => c.presence === "featured",
    effect: { kind: "reject", reason_code: "already_featured" },
  },
  {
    policy_id: "reject-weak-gap",
    version: "0.1.0",
    description:
      "Very small demand + already-present visibility isn't worth surfacing.",
    applies_to: (c) =>
      c.intent.demand_strength < 0.15 && (c.presence === "present" || c.presence === "featured"),
    effect: { kind: "reject", reason_code: "weak_gap" },
  },

  // --- Boosts ----------------------------------------------------------
  {
    policy_id: "boost-goal-aligned",
    version: "0.1.0",
    description:
      "Opportunities aligned with the retailer's stated goals rank higher than generic demand.",
    applies_to: (c, ctx) => {
      const hay = intentBag(c);
      return ctx.goals.some((g) => shareStem(hay, g.label));
    },
    effect: { kind: "boost", delta: 1.0, reason: "aligned with a stated goal" },
  },
  {
    policy_id: "boost-strength-match",
    version: "0.1.0",
    description:
      "When an intent overlaps a real strength, closing the gap is a fast win — boost it.",
    applies_to: (c, ctx) => {
      const hay = intentBag(c);
      return ctx.strengths.some((s) => shareStem(hay, s.label));
    },
    effect: { kind: "boost", delta: 0.6, reason: "overlaps a stated strength" },
  },
  {
    policy_id: "boost-rising-local",
    version: "0.1.0",
    description:
      "Rising local demand deserves attention this season, not next year.",
    applies_to: (c) => c.intent.trend === "rising" && c.intent.local_confidence >= 0.7,
    effect: { kind: "boost", delta: 0.4, reason: "rising with strong local confidence" },
  },

  // --- Downweights -----------------------------------------------------
  {
    policy_id: "downweight-falling-demand",
    version: "0.1.0",
    description: "Demand that is fading shouldn't outrank demand that is growing.",
    applies_to: (c) => c.intent.trend === "falling",
    effect: { kind: "downweight", delta: 0.5, reason: "demand is falling" },
  },
  {
    policy_id: "downweight-low-local",
    version: "0.1.0",
    description:
      "Non-local demand is real but not the retailer's audience — downweight it.",
    applies_to: (c) => c.intent.local_confidence < 0.4,
    effect: { kind: "downweight", delta: 0.4, reason: "low local confidence" },
  },

  // --- Horizon ---------------------------------------------------------
  {
    policy_id: "horizon-rising-local-this-week",
    version: "0.1.0",
    description:
      "A rising, high-local, absent intent is a this-week move — it's leaking customers now.",
    applies_to: (c) =>
      c.intent.trend === "rising" &&
      c.intent.local_confidence >= 0.7 &&
      c.presence === "absent",
    effect: { kind: "set_horizon", horizon: "this_week", reason: "rising local demand with no presence" },
  },
];